/**
 * FleetSparkline — pure SVG sparkline strip for fleet property cards.
 * Renders last 24h of SoC%, Solar kW, Load kW as three stacked mini-lines.
 * No charting library dependency. <2KB gzipped.
 *
 * Data source: `get_fleet_sparkline_data` RPC, which aggregates from raw
 * weekly-partitioned `telemetry` table. NOT from `telemetry_hourly` — that
 * table only contains archival data >7 days old (rollup-telemetry RETENTION_DAYS=7).
 *
 * Design rules:
 * - SoC%: brand-accent (teal), 0–100 fixed domain
 * - Solar kW: solar-gold, 0–max dynamic domain
 * - Load kW: text-secondary, 0–max dynamic domain
 * - Height: ≤40px total, 3 sparklines stacked with 1px gap
 * - Stale data: 50% opacity
 * - Empty: "No data" label centered
 */

import { useMemo, useState, useCallback, useRef } from 'react';
import { cn } from '@/lib/utils';

export interface SparklinePoint {
  /** ISO timestamp of the hourly bucket */
  bucket: string;
  /** Average SoC % (0–100) */
  avgSoc: number | null;
  /** Average PV power in kW */
  avgPvKw: number | null;
  /** Average load power in kW */
  avgLoadKw: number | null;
}

export type SparklineStatus = 'empty' | 'stale' | 'healthy' | 'warning' | 'critical' | 'alert-active';

interface FleetSparklineProps {
  /** Pre-queried 24h hourly data points (up to 24). */
  data: SparklinePoint[];
  /** Visual status — controls accent bar and opacity. */
  status: SparklineStatus;
  /** Width of the sparkline strip in px. Default 120. */
  width?: number;
  /** Click handler — deep-link to property detail. */
  onClick?: (e: React.MouseEvent) => void;
  className?: string;
}

const STRIP_HEIGHT = 40;
const LINE_HEIGHT = 11; // 3 lines × 11px + 2 × 1px gap = 35px, leaves 5px padding
const GAP = 1;
const SOC_MAX = 100;

/** Map status to left-border accent color class (matches FleetSystemCard convention). */
function statusAccentClass(status: SparklineStatus): string {
  switch (status) {
    case 'critical':
    case 'alert-active':
      return 'border-l-[2px] border-l-wt-red-primary/70';
    case 'warning':
      return 'border-l-[2px] border-l-wt-amber-primary/60';
    default:
      return '';
  }
}

/** Build SVG polyline points string from data array. */
function buildPolyline(
  data: SparklinePoint[],
  accessor: (p: SparklinePoint) => number | null,
  yMin: number,
  yMax: number,
  width: number,
  yOffset: number,
): string {
  if (data.length === 0) return '';
  const range = yMax - yMin || 1;
  const xStep = data.length > 1 ? width / (data.length - 1) : 0;
  const points: string[] = [];
  for (let i = 0; i < data.length; i++) {
    const raw = accessor(data[i]);
    const val = raw ?? 0;
    const x = (i * xStep).toFixed(1);
    // Invert Y: SVG y=0 is top
    const y = (yOffset + LINE_HEIGHT - ((val - yMin) / range) * LINE_HEIGHT).toFixed(1);
    points.push(`${x},${y}`);
  }
  return points.join(' ');
}

/** Compute dynamic max for a series, minimum 1 to avoid div-by-zero. */
function dynamicMax(data: SparklinePoint[], accessor: (p: SparklinePoint) => number | null): number {
  let max = 0;
  for (const d of data) {
    const v = accessor(d);
    if (v != null && v > max) max = v;
  }
  return Math.max(max, 1);
}

export function FleetSparkline({
  data,
  status,
  width = 120,
  onClick,
  className,
}: FleetSparklineProps) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const pvMax = useMemo(() => dynamicMax(data, (p) => p.avgPvKw), [data]);
  const loadMax = useMemo(() => dynamicMax(data, (p) => p.avgLoadKw), [data]);

  const socLine = useMemo(
    () => buildPolyline(data, (p) => p.avgSoc, 0, SOC_MAX, width, 2),
    [data, width],
  );
  const pvLine = useMemo(
    () => buildPolyline(data, (p) => p.avgPvKw, 0, pvMax, width, 2 + LINE_HEIGHT + GAP),
    [data, width, pvMax],
  );
  const loadLine = useMemo(
    () => buildPolyline(data, (p) => p.avgLoadKw, 0, loadMax, width, 2 + (LINE_HEIGHT + GAP) * 2),
    [data, width, loadMax],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (!svgRef.current || data.length === 0) return;
      const rect = svgRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const idx = Math.round((x / width) * (data.length - 1));
      setHoverIndex(Math.max(0, Math.min(data.length - 1, idx)));
    },
    [data, width],
  );

  const handleMouseLeave = useCallback(() => setHoverIndex(null), []);

  // Empty state
  if (data.length === 0 || status === 'empty') {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded bg-wt-bg-tertiary/50',
          className,
        )}
        style={{ width, height: STRIP_HEIGHT }}
      >
        <span className="text-[9px] text-wt-text-disabled">No data</span>
      </div>
    );
  }

  const isStale = status === 'stale';
  const hoverPoint = hoverIndex != null ? data[hoverIndex] : null;
  const hoverX = hoverIndex != null && data.length > 1
    ? (hoverIndex / (data.length - 1)) * width
    : null;

  return (
    <div
      className={cn(
        'relative group',
        isStale && 'opacity-50',
        statusAccentClass(status),
        className,
      )}
      style={{ width, height: STRIP_HEIGHT }}
      onClick={onClick}
    >
      <svg
        ref={svgRef}
        width={width}
        height={STRIP_HEIGHT}
        viewBox={`0 0 ${width} ${STRIP_HEIGHT}`}
        className="block"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        aria-label="24h telemetry sparkline"
      >
        {/* SoC% line — brand-accent teal */}
        {socLine && (
          <polyline
            points={socLine}
            fill="none"
            stroke="hsl(var(--brand-accent))"
            strokeWidth="1.5"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        )}
        {/* Solar kW line — solar-gold */}
        {pvLine && (
          <polyline
            points={pvLine}
            fill="none"
            stroke="hsl(var(--solar-gold))"
            strokeWidth="1"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        )}
        {/* Load kW line — text-secondary */}
        {loadLine && (
          <polyline
            points={loadLine}
            fill="none"
            stroke="hsl(var(--text-secondary))"
            strokeWidth="1"
            strokeLinejoin="round"
            strokeLinecap="round"
            opacity={0.7}
          />
        )}
        {/* Hover crosshair */}
        {hoverX != null && (
          <line
            x1={hoverX}
            y1={0}
            x2={hoverX}
            y2={STRIP_HEIGHT}
            stroke="hsl(var(--text-muted))"
            strokeWidth="0.5"
            strokeDasharray="2 2"
          />
        )}
      </svg>

      {/* Tooltip on hover */}
      {hoverPoint && hoverX != null && (
        <div
          className="absolute z-20 pointer-events-none bg-popover border border-border rounded px-2 py-1.5 shadow-lg"
          style={{
            bottom: STRIP_HEIGHT + 4,
            left: Math.min(hoverX, width - 80),
            whiteSpace: 'nowrap',
          }}
        >
          <div className="text-[9px] text-wt-text-muted mb-0.5">
            {formatHour(hoverPoint.bucket)}
          </div>
          <div className="flex flex-col gap-0.5 text-[10px]">
            <span>
              <span className="text-brand-accent font-mono-numeric">
                {hoverPoint.avgSoc != null ? `${hoverPoint.avgSoc.toFixed(0)}%` : '—'}
              </span>
              <span className="text-wt-text-muted ml-1">SoC</span>
            </span>
            <span>
              <span className="text-wt-solar-gold font-mono-numeric">
                {hoverPoint.avgPvKw != null ? `${hoverPoint.avgPvKw.toFixed(1)}` : '—'}
              </span>
              <span className="text-wt-text-muted ml-1">kW pv</span>
            </span>
            <span>
              <span className="text-wt-text-secondary font-mono-numeric">
                {hoverPoint.avgLoadKw != null ? `${hoverPoint.avgLoadKw.toFixed(1)}` : '—'}
              </span>
              <span className="text-wt-text-muted ml-1">kW load</span>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function formatHour(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
  } catch {
    return iso;
  }
}
